import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  auditTime,
  catchError,
  tap,
  map,
  of,
  animationFrameScheduler,
  distinctUntilChanged,
} from "rxjs";
import PropTypes from "prop-types";
import Track from "../../models/Track";
import CoverageLine from "./models/CoverageLine";

function CoverageOverlay({ overlayData, track }) {
  const containerRef = useRef(null);
  const { mainLines } = overlayData;
  const [label, setLabel] = useState({ val: -1, genPos: 0, analysis: "" });

  const calcPos = useCallback(
    (pxPos) => {
      const { scale } = track.zoomService.xVisScale$.value;
      const genPos = pxPos > 0 ? scale.invert(pxPos) : -1;
      return Math.floor(genPos);
    },
    [track]
  );

  const recalcLabels = useCallback(
    (genPos, lines) => {
      const empty = { val: -1, analysis: "" };

      const found = (lines || [])
        .filter((x) => x.region.contains(genPos))
        .map(({ points, analysis }) => ({
          analysis,
          point: points.find((x) => x.region.contains(genPos)),
        }))
        .filter((x) => x.point)
        .map(({ analysis, point }) => ({ analysis, val: point.val }))
        .pop();

      setLabel(found || empty);
    },
    [setLabel, track]
  );

  useEffect(() => {
    function reposition(pxPos) {
      if (!pxPos || !containerRef.current) return;
      containerRef.current.style.transform = `translateX(${pxPos}px)`;
    }

    const sub = track.zoomService.mousePosX$
      .pipe(
        distinctUntilChanged(),
        auditTime(0, animationFrameScheduler),
        tap(reposition),
        map((x) => calcPos(x)),
        distinctUntilChanged(),
        auditTime(600),
        tap((x) => recalcLabels(x, mainLines)),
        catchError(() => of(null))
      )
      .subscribe();

    return () => sub.unsubscribe();
  }, [mainLines, recalcLabels]);

  return (
    <div
      ref={containerRef}
      className="tw-pointer-events-none tw-text-3xs tw-relative color-1 tw-px-1"
    >
      {label && label.val !== -1 ? (
        <div className="anim-fade-in anim-duration-200">
          <div className="tw-opacity-50 tw-inline-block">COVERAGE:</div>
          <div className="tw-inline-block tw-px-2 tw-font-bold">
            {label?.val}
          </div>
          <div className="tw-opacity-50 tw-inline-block">SAMPLE:</div>
          <div className="tw-inline-block tw-px-2 tw-font-bold">
            {label?.analysis}
          </div>
        </div>
      ) : null}
    </div>
  );
}

CoverageOverlay.propTypes = {
  overlayData: PropTypes.shape({
    mainLines: PropTypes.arrayOf(PropTypes.instanceOf(CoverageLine)),
  }),
  track: PropTypes.instanceOf(Track).isRequired,
};

CoverageOverlay.defaultProps = {
  overlayData: {
    mainLines: [],
  },
};

export default CoverageOverlay;
